import { SymbolHeader } from '@src/components/SymbolDisplay/SymbolHeader';
import type { SymbolDisplayProps } from '@src/components/SymbolDisplay/types';
import { SymbolSelector } from '@src/components/SymbolSelector';
import { useSymbolSelectorWidth } from '@src/components/SymbolSelector/useSymbolSelectorWidth';
import {
  FormControlSizes,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  ProductTypeEnum,
  VStack,
  useMixpanel,
  type MarketDataCardConfig,
  type Security,
} from '@talos/kyoko';
import type { UseComboboxStateChange } from 'downshift';
import { memo, useCallback, useState } from 'react';
import { useMarketDataCardHeaderSymbolChange } from './useMarketDataCardHeaderSymbolChange';

/** Product types Market Data Card symbol selectors should never offer. */
export const MARKET_DATA_CARD_EXCLUDED_PRODUCT_TYPES: readonly ProductTypeEnum[] = [
  ProductTypeEnum.PredictionOutcome,
  ProductTypeEnum.OTC,
];

interface MarketDataCardSymbolSelectorProps {
  config: MarketDataCardConfig;
  symbol: string | undefined;
  symbolHeaderProps: SymbolDisplayProps;
  onSaveCard: (config: MarketDataCardConfig) => void;
  onOpenMarketSelectorFromHeaderButton: () => void;
}

interface MarketDataCardSymbolLabelProps {
  symbolHeaderProps: SymbolDisplayProps;
  onActivate: () => void;
}

const MarketDataCardSymbolLabel = memo(function MarketDataCardSymbolLabel({
  symbolHeaderProps,
  onActivate,
}: MarketDataCardSymbolLabelProps) {
  return (
    <VStack
      alignItems="center"
      gap={0}
      cursor="pointer"
      role="button"
      tabIndex={0}
      aria-haspopup="dialog"
      onClick={onActivate}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onActivate();
        }
      }}
      data-testid="market-card-symbol-selector-tour-anchor"
    >
      <SymbolHeader {...symbolHeaderProps} />
    </VStack>
  );
});

interface MarketDataCardSymbolSelectorActiveProps extends MarketDataCardSymbolSelectorProps {
  onClose: () => void;
}

const MarketDataCardSymbolSelectorActive = memo(function MarketDataCardSymbolSelectorActive({
  config,
  symbol,
  symbolHeaderProps,
  onSaveCard,
  onOpenMarketSelectorFromHeaderButton,
  onClose,
}: MarketDataCardSymbolSelectorActiveProps) {
  const symbolSelectorDropdownWidth = useSymbolSelectorWidth();
  const handleMarketCardSymbolChange = useMarketDataCardHeaderSymbolChange({
    config,
    onSaveCard,
    onOpenMarketSelectorFromHeaderButton,
  });

  const handleHeaderSymbolSelectorOpenChange = useCallback(
    (changes: UseComboboxStateChange<Security>) => {
      if (!changes.isOpen) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <SymbolSelector
      symbol={symbol ?? config.symbol}
      onSymbolChanged={handleMarketCardSymbolChange}
      size={FormControlSizes.Small}
      dropdownWidth={symbolSelectorDropdownWidth}
      dropdownPlacement="bottom-end"
      showGroups
      showFavorites
      portalize
      startOpen
      excludeProductTypes={MARKET_DATA_CARD_EXCLUDED_PRODUCT_TYPES}
      dropdownId="market-card-header-symbol-selector"
      onIsOpenChange={handleHeaderSymbolSelectorOpenChange}
      renderTrigger={({ openMenu }) => (
        <MarketDataCardSymbolLabel symbolHeaderProps={symbolHeaderProps} onActivate={openMenu} />
      )}
    />
  );
});

export const MarketDataCardSymbolSelector = memo(function MarketDataCardSymbolSelector({
  config,
  symbol,
  symbolHeaderProps,
  onSaveCard,
  onOpenMarketSelectorFromHeaderButton,
}: MarketDataCardSymbolSelectorProps) {
  const mixpanel = useMixpanel();
  const [isSelectorActive, setIsSelectorActive] = useState(false);

  const handleActivateSelector = useCallback(() => {
    mixpanel.track(MixpanelEvent.OpenSymbolSelectorFromMarketCard, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCardHeader,
    });
    setIsSelectorActive(true);
  }, [mixpanel]);

  const handleCloseSelector = useCallback(() => {
    setIsSelectorActive(false);
  }, []);

  if (!isSelectorActive) {
    return <MarketDataCardSymbolLabel symbolHeaderProps={symbolHeaderProps} onActivate={handleActivateSelector} />;
  }

  return (
    <MarketDataCardSymbolSelectorActive
      config={config}
      symbol={symbol}
      symbolHeaderProps={symbolHeaderProps}
      onSaveCard={onSaveCard}
      onOpenMarketSelectorFromHeaderButton={onOpenMarketSelectorFromHeaderButton}
      onClose={handleCloseSelector}
    />
  );
});
